import { useEffect, useState } from 'react'
import type { Attachment, DocumentReference } from '@medplum/fhirtypes'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

// Provider-scoped read through the middleware oRPC (consentGet with a patientId —
// Practitioner-only, IDOR-guarded server-side). `signed` is meaningful only once
// `loading` is false; callers must gate on `loading`.
export function useConsentStatus(patientId: string) {
  const client = useClient()
  const [signed, setSigned] = useState(false)
  // Attachment of the signed consent document — populated only when signed.
  const [attachment, setAttachment] = useState<Attachment | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!patientId) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    client
      .consentGet({ patientId })
      .then((res) => {
        if (cancelled) return
        // Patient-level rollup across orgs: any granted (active) consent → signed.
        const granted = (res.items ?? []).find((item) => item.granted)
        setSigned(Boolean(granted))
        setAttachment(
          (granted?.document as DocumentReference | undefined)?.content?.[0]?.attachment,
        )
      })
      .catch((err) => {
        logError('ConsentStatus', 'load consent status', err)
        if (!cancelled) {
          setSigned(false)
          setAttachment(undefined)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, patientId])

  return { signed, loading, attachment }
}
